/**
 * QOE-CPA-FIXPACK-1 D5 — industry is asked twice, so answer it once.
 *
 * The New Engagement form captures `vertical` (one of `lib/verticals.ts`
 * VERTICALS; source of truth `core/qoe/industry_benchmarks.py`). The scope
 * wizard then asks "What is the primary industry?" again, but for a different
 * purpose: a 1–3 COMPLEXITY WEIGHT that feeds the tier score.
 *
 * Those are two different questions wearing the same word. The engagement's
 * `vertical` is industry IDENTITY and stays the source of truth — it drives the
 * benchmark card and the seller document-request overlay, so it cannot be
 * dropped. This map derives the wizard's weight from it, so the CPA answers
 * industry once and can still override when a particular deal is unusually
 * simple or hairy.
 *
 * The weights mirror the wizard's own option labels:
 *   1 — Service / Professional Services
 *   2 — Construction / HVAC / Field Services
 *   3 — Manufacturing / O&G / Healthcare / Multi-vertical
 *
 * `Other / General SMB` is deliberately absent: "unknown industry" is not a
 * complexity signal, and guessing one would silently answer a question nobody
 * answered. An unmapped or missing vertical simply leaves the question blank.
 */

/** The wizard question this map prefills. Kept as a constant so the component
 *  and the map cannot drift apart on a rename. */
export const INDUSTRY_QUESTION_ID = 'industry'

const COMPLEXITY_BY_VERTICAL: Record<string, 1 | 2 | 3> = {
  // 1 — service / professional. Few entities, light inventory, clean books.
  'Professional Services (General)': 1,
  'Accounting / Tax / CPA Firm': 1,
  'IT / Managed Services (MSP)': 1,
  'Staffing Agency': 1,
  'Insurance Agency': 1,
  'Real Estate Brokerage': 1,
  'Childcare / Daycare': 1,
  'Fitness / Gym': 1,

  // 2 — field services and trade. Job costing, WIP, fleet, or inventory turn.
  'HVAC / Mechanical Contractors': 2,
  'Construction / General Contractor': 2,
  'Trucking / Freight': 2,
  'Auto Repair': 2,
  'Landscaping / Lawn Care': 2,
  'Restaurant / QSR': 2,
  'E-Commerce / Online Retail': 2,

  // 3 — manufacturing and regulated healthcare. Absorption costing, standard
  // costs, or third-party payor revenue recognition.
  'Manufacturing (Job Shop)': 3,
  'Dental Practice': 3,
  'Healthcare (Home Health)': 3,
  'Physical Therapy / Outpatient': 3,
}

/**
 * The scope wizard's complexity weight for an engagement vertical, or null when
 * there is nothing defensible to prefill (no vertical, `Other / General SMB`,
 * or a string this map has not seen). Null means "ask the question" — never
 * fall back to a default weight, because a wrong prefill is worse than an
 * unanswered one: it silently moves the tier.
 */
export function complexityForVertical(vertical?: string | null): 1 | 2 | 3 | null {
  if (!vertical) return null
  return COMPLEXITY_BY_VERTICAL[vertical.trim()] ?? null
}

/**
 * QOE-TRUTH-AND-JOURNEY-1 R6 — the human-readable form of a resolved weight,
 * for the "Dental Practice → Complexity: High (3 of 3)" fact line. Mirrors the
 * wizard's own option labels (1=Low/Service, 2=Medium/Field Services,
 * 3=High/Manufacturing) so the resolved fact and the fallback question never
 * disagree on what a number means.
 */
export function complexityLabel(weight: 1 | 2 | 3): string {
  const names: Record<1 | 2 | 3, string> = { 1: 'Low', 2: 'Medium', 3: 'High' }
  return `${names[weight]} (${weight} of 3)`
}
